import { useCallback } from 'react';

import { AssetItem, RecommendedAsset } from 'config/api/actionLibrary/actionLibrary.types';

const isRecommendedAsset = (asset: RecommendedAsset | AssetItem): asset is RecommendedAsset =>
  'owner' in asset && 'profile' in (asset as RecommendedAsset).owner;

export type UseAssetToggleHandlersParams = {
  toggleRecommended: (asset: RecommendedAsset) => void;
  toggleAvailable: (asset: AssetItem) => void;
};

export type UseAssetToggleHandlersResult = {
  handleToggleRecommended: (asset: RecommendedAsset | AssetItem) => void;
  handleToggleAvailable: (asset: RecommendedAsset | AssetItem) => void;
};

/**
 * Returns toggle handlers that accept union type (RecommendedAsset | AssetItem)
 * and delegate to the correct toggle based on type guard.
 */
export const useAssetToggleHandlers = ({
  toggleRecommended,
  toggleAvailable,
}: UseAssetToggleHandlersParams): UseAssetToggleHandlersResult => {
  const handleToggleRecommended = useCallback(
    (asset: RecommendedAsset | AssetItem) => {
      if (isRecommendedAsset(asset)) {
        toggleRecommended(asset);
      }
    },
    [toggleRecommended],
  );

  const handleToggleAvailable = useCallback(
    (asset: RecommendedAsset | AssetItem) => {
      if (!isRecommendedAsset(asset)) {
        toggleAvailable(asset);
      }
    },
    [toggleAvailable],
  );

  return { handleToggleRecommended, handleToggleAvailable };
};
